import { isPhoneMissing } from '@shared/phone-quality';
import {
  AlertTriangle,
  CalendarDays,
  CheckCheck,
  ChevronRight,
  Clock3,
  Copy,
  LifeBuoy,
  Loader2,
  PhoneCall,
  PhoneOff,
  Repeat2,
  ShieldAlert,
  Star,
  UserCheck,
  X,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { usePipelineContextSafe } from '../../context';
import { DealValueDisplay } from '@/components/pipeline/DealValueDisplay';
import { CallButton } from '@/components/calling/CallButton';
import { PipelineCardContact } from '../PipelineCard/PipelineCardContact';
import { PipelineCardProperty } from '../PipelineCard/PipelineCardProperty';
import { PipelineCardLeadInfo } from '../PipelineCard/PipelineCardLeadInfo';
import { PipelineCardNotes } from '../PipelineCard/PipelineCardNotes';
import { PipelineCardActions } from '../PipelineCard/PipelineCardActions';
import { PipelineAppointmentSummary } from '../PipelineCard/PipelineAppointmentSummary';
import { PipelineWhat3wordsField } from './PipelineWhat3wordsField';
import {
  getEnquiryAgeBadgeClasses,
  getEnquiryAgeLabel,
} from '../PipelineLayout/UnassignedEnquiryQueue';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import type { Customer } from '../../types';

interface PipelineDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: Customer | null;
}

/**
 * Slide-out detail sheet for viewing/editing customer information
 * Replaces inline card expansion for better UX and no z-index issues
 */
export function PipelineDetailSheet({
  open,
  onOpenChange,
  customer,
}: PipelineDetailSheetProps) {
  // Use safe hook - returns undefined if context not available (portal edge case during HMR)
  const context = usePipelineContextSafe();

  // Early return if no customer or no context (portal edge case)
  if (!customer || !context) return null;

  const { actions, meta } = context;

  // Get stage info
  const stage = meta.stageById.get(customer.pipelineStageId ?? 0);
  const stageName = stage?.name ?? 'Unknown Stage';
  const stageColor = stage?.color ?? '#6B7280';

  // Convert leadScore to 1-5 star scale (leadScore is 0-100, we use it as star count 1-5)
  // If leadScore is stored as 1-5 directly, use it; otherwise map from 0-100 scale
  const starScore = customer.leadScore !== null && customer.leadScore !== undefined
    ? (customer.leadScore <= 5 ? customer.leadScore : Math.round(customer.leadScore / 20))
    : 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="!w-[420px] !max-w-[420px] overflow-y-auto p-0"
      >
        {/* Header with customer name and stage badge */}
        <div
          className="sticky top-0 z-10 p-6 pb-4 border-b bg-white dark:bg-slate-900"
          style={{
            borderBottomColor: stageColor,
            borderTopWidth: '3px',
            borderTopColor: stageColor,
          }}
        >
          {/* Custom close button - positioned above sticky header */}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="absolute right-4 top-4 z-20 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>

          <SheetHeader className="space-y-3">
            <div className="flex items-start justify-between gap-4 pr-8">
              <div className="flex-1 min-w-0">
                {/* Customer Name with Copy Button */}
                <div className="flex items-center gap-2">
                  <SheetTitle className="text-xl font-semibold truncate">
                    {customer.name}
                  </SheetTitle>
                  <button
                    type="button"
                    className="focus:outline-none opacity-70 hover:opacity-100 transition-opacity"
                    onClick={() => meta.copyToClipboard(customer.name, `sheet-name-${customer.id}`)}
                    title="Copy name"
                  >
                    {meta.isCopied[`sheet-name-${customer.id}`] ? (
                      <CheckCheck className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4 text-slate-400" />
                    )}
                  </button>
                </div>

                {/* Star Rating - Clickable */}
                <div className="flex items-center gap-0.5 mt-2">
                  {[1, 2, 3, 4, 5].map((score) => (
                    <button
                      key={score}
                      type="button"
                      className="focus:outline-none transition-all duration-200 hover:scale-110"
                      onClick={() => actions.updateLeadScore(customer.id, score)}
                      title={`Rate ${score} star${score !== 1 ? 's' : ''}`}
                    >
                      <Star className={`h-5 w-5 ${
                        starScore >= score
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300 dark:text-gray-600 hover:text-yellow-300'
                      }`} />
                    </button>
                  ))}
                </div>

                {/* Editable Deal Value */}
                <div className="mt-3">
                  <DealValueDisplay
                    customerId={customer.id}
                    dealValue={customer.dealValue ?? null}
                    additionalData={customer.additionalData}
                    isCompact={false}
                  />
                </div>
              </div>
            </div>

            {/* Stage badge and source */}
            <div className="flex items-center gap-2 flex-wrap">
              <Badge
                variant="secondary"
                className="text-xs font-medium"
                style={{
                  backgroundColor: `${stageColor}20`,
                  color: stageColor,
                  borderColor: `${stageColor}40`,
                }}
              >
                {stageName}
              </Badge>
              {customer.source && (
                <Badge variant="outline" className="text-xs">
                  {customer.source}
                </Badge>
              )}
            </div>
          </SheetHeader>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {customer.enquiryAttention && (
            <AssignedEnquiryAttention customer={customer} />
          )}

          {/* Primary Actions: Call and Book Appointment */}
          <div className="pb-4 border-b border-slate-100 dark:border-slate-700/60 space-y-3">
            {isPhoneMissing(customer.phone) && (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                Phone missing. Add the customer's number when you have it.
              </p>
            )}
            {/* Call Button - Prominent */}
            {customer.phone && !isPhoneMissing(customer.phone) && (
              <CallButton
                customerId={customer.id}
                customerName={customer.name}
                customerPhone={customer.phone}
                size="lg"
                variant="default"
                showLabel={true}
                className="w-full bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700 text-white shadow-sm"
              />
            )}
            {/* Book Appointment */}
            <Button
              variant="default"
              size="lg"
              className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white shadow-sm"
              onClick={() => {
                actions.openBookingPopup(customer);
              }}
            >
              <CalendarDays className="mr-2 h-5 w-5" />
              Book Appointment
            </Button>
          </div>

          <PipelineAppointmentSummary customer={customer} variant="detail" />

          {/* Contact Information */}
          <section>
            <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">
              Contact Information
            </h3>
            <PipelineCardContact
              customer={customer}
              highlightTerms={meta.highlightTerms}
              isCopied={meta.isCopied}
              copyToClipboard={meta.copyToClipboard}
            />
          </section>

          {/* Property Information */}
          <section>
            <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">
              Property Details
            </h3>
            <PipelineCardProperty customer={customer} />
            <PipelineWhat3wordsField
              customer={customer}
              isSaving={meta.isUpdating}
              copiedState={meta.isCopied}
              onCopy={meta.copyToClipboard}
              onSave={(what3wordsAddress) =>
                actions.updateCustomer(customer.id, { what3wordsAddress })
              }
            />
          </section>

          {/* Lead Information */}
          <section>
            <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">
              Lead Information
            </h3>
            <PipelineCardLeadInfo customer={customer} />
          </section>

          {/* Notes */}
          <section>
            <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">
              Notes
            </h3>
            <PipelineCardNotes customer={customer} />
          </section>

          {/* Actions */}
          <section className="pt-4 border-t border-slate-100 dark:border-slate-700/60">
            <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">
              Quick Actions
            </h3>
            <PipelineCardActions customer={customer} />
          </section>

          {/* View Full Profile Link */}
          <div className="pt-4">
            <a
              href={`/customers/${customer.id}`}
              className="flex items-center justify-center gap-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              View Full Customer Profile
              <ChevronRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

type EnquiryContactAction = 'no_answer' | 'contacted';

function AssignedEnquiryAttention({ customer }: { customer: Customer }) {
  const attention = customer.enquiryAttention;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const contactMutation = useMutation({
    mutationFn: async (action: EnquiryContactAction) => {
      if (!attention) throw new Error('No active enquiry was found for this customer.');

      if (action === 'no_answer') {
        return apiRequest(
          'POST',
          `/api/pipeline/enquiries/${encodeURIComponent(attention.enquiryId)}/contact-attempt`,
          { outcome: 'no_answer' }
        );
      }

      return apiRequest(
        'POST',
        `/api/pipeline/enquiries/${encodeURIComponent(attention.enquiryId)}/contacted`,
        {}
      );
    },
    onSuccess: async (_response, action) => {
      toast({
        title: action === 'contacted' ? 'Customer contact confirmed' : 'No answer recorded',
        description:
          action === 'contacted'
            ? 'The enquiry has left the attention view because customer contact is now confirmed.'
            : 'The attempt is recorded, but the enquiry remains in its current attention state.',
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['/api/pipeline/enquiries/queue'] }),
        queryClient.invalidateQueries({ queryKey: ['/api/pipeline/enquiries/queue/count'] }),
        queryClient.invalidateQueries({ queryKey: ['/api/pipeline-stages/data'] }),
      ]);
    },
    onError: (error: Error) => {
      toast({
        title: 'Contact update failed',
        description: error.message || 'Please refresh and try again.',
        variant: 'destructive',
      });
    },
  });

  if (!attention) return null;

  const receivedAt = new Date(attention.receivedAt);
  const receivedAge = Number.isNaN(receivedAt.getTime())
    ? 'Received time unavailable'
    : `Received ${formatDistanceToNow(receivedAt, { addSuffix: true })}`;
  const isNoAnswerPending =
    contactMutation.isPending && contactMutation.variables === 'no_answer';
  const isContactedPending =
    contactMutation.isPending && contactMutation.variables === 'contacted';

  return (
    <section className="rounded-lg border border-slate-200 bg-slate-50/70 p-4 dark:border-slate-700 dark:bg-slate-800/40">
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          variant="outline"
          className={`gap-1 text-[10px] ${getEnquiryAgeBadgeClasses(attention.ageState)}`}
        >
          {attention.ageState === 'needs_attention' ? (
            <AlertTriangle className="h-3 w-3" />
          ) : (
            <Clock3 className="h-3 w-3" />
          )}
          {getEnquiryAgeLabel(attention.ageState)}
        </Badge>
        {attention.classification === 'repeat_enquiry' && (
          <Badge variant="outline" className="gap-1 text-[10px]">
            <Repeat2 className="h-3 w-3" />
            Repeat enquiry
          </Badge>
        )}
        {(attention.identityReviewRequired ||
          attention.identityReviewStatus !== 'not_required') && (
          <Badge
            variant="outline"
            className="gap-1 border-violet-200 bg-violet-50 text-[10px] text-violet-700 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-300"
          >
            <ShieldAlert className="h-3 w-3" />
            Identity review
          </Badge>
        )}
        {attention.rescued && (
          <Badge variant="outline" className="gap-1 text-[10px]">
            <LifeBuoy className="h-3 w-3" />
            Rescued
          </Badge>
        )}
      </div>

      <div className="mt-3 space-y-2 text-sm">
        <div className="flex items-center justify-between gap-3">
          <span className="text-muted-foreground">Assigned</span>
          <span className="flex items-center gap-1.5 font-medium">
            <UserCheck className="h-3.5 w-3.5 text-blue-500" />
            {attention.assignee?.name || 'Assigned to you'}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-muted-foreground">Customer contacted</span>
          <span className="font-medium">
            {attention.firstContactedAt ? 'Confirmed' : 'Not yet'}
          </span>
        </div>
        <div className="text-xs text-muted-foreground">{receivedAge}</div>
      </div>

      {!attention.firstContactedAt && (
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={contactMutation.isPending}
            onClick={() => contactMutation.mutate('no_answer')}
          >
            {isNoAnswerPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <PhoneOff className="mr-2 h-4 w-4" />
            )}
            No answer
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={contactMutation.isPending}
            onClick={() => contactMutation.mutate('contacted')}
          >
            {isContactedPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <PhoneCall className="mr-2 h-4 w-4" />
            )}
            Contacted
          </Button>
        </div>
      )}
    </section>
  );
}
